// Runtime detector for a newer deployed build.
// Reads the current bundle's BUILD_ID (injected by Vite via `define`)
// and periodically compares it with the BUILD_ID advertised by the
// latest /index.html on the server. When they diverge, we surface an
// "Update Thiia" button (via subscribeUpdateAvailable) instead of a
// silent toast, so the user has an explicit action to reload with a
// full cache purge — or we silently reload if the tab has been hidden
// long enough that the user won't lose context.

import { invalidate as invalidateChecklistsCache } from "@/hooks/checklistsCache";
import { clearEmailCache } from "@/lib/emailSearchCache";

declare const __BUILD_ID__: string;

const CURRENT_BUILD_ID: string =
  typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "dev";

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 min
const HIDDEN_AUTO_RELOAD_MS = 30 * 60 * 1000; // 30 min hidden → silent reload

let started = false;
let lastHiddenAt: number | null = null;
let updateAvailable = false;

type Listener = (available: boolean) => void;
const listeners = new Set<Listener>();

function notify() {
  for (const cb of listeners) {
    try {
      cb(updateAvailable);
    } catch {
      /* isolated listener error */
    }
  }
}

export function subscribeUpdateAvailable(cb: Listener): () => void {
  listeners.add(cb);
  // Fire once so subscribers sync with current state.
  try {
    cb(updateAvailable);
  } catch {
    /* ignore */
  }
  return () => {
    listeners.delete(cb);
  };
}

export function getUpdateAvailable(): boolean {
  return updateAvailable;
}

function setUpdateAvailable(v: boolean) {
  if (updateAvailable === v) return;
  updateAvailable = v;
  notify();
}

async function fetchRemoteBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`/index.html?ts=${Date.now()}`, {
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!res.ok) return null;
    const html = await res.text();
    const match = html.match(
      /<meta\s+name=["']build-id["']\s+content=["']([^"']+)["']/i,
    );
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

async function checkOnce() {
  const remote = await fetchRemoteBuildId();
  if (!remote || remote === CURRENT_BUILD_ID) return;

  // If the tab has been hidden long enough, reload silently with a full purge.
  if (
    document.visibilityState === "hidden" &&
    lastHiddenAt !== null &&
    Date.now() - lastHiddenAt > HIDDEN_AUTO_RELOAD_MS
  ) {
    void performFullUpdate();
    return;
  }

  setUpdateAvailable(true);
}

/**
 * Full-purge update flow triggered by the "Mettre à jour Thiia" button.
 * Clears every browser cache (Cache Storage), unregisters service workers,
 * flushes in-memory app caches, drops volatile `thiia:cache:*` localStorage
 * entries (auth / prefs / drafts / panels are preserved), then hard-reloads
 * with a cache-busting query param so the browser fetches the fresh HTML.
 */
export async function performFullUpdate(): Promise<void> {
  try {
    if (typeof window !== "undefined" && "caches" in window) {
      const keys = await caches.keys().catch(() => [] as string[]);
      await Promise.allSettled(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* ignore */
  }
  try {
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker
        .getRegistrations()
        .catch(() => [] as ServiceWorkerRegistration[]);
      await Promise.allSettled(regs.map((r) => r.unregister()));
    }
  } catch {
    /* ignore */
  }
  try {
    invalidateChecklistsCache();
    clearEmailCache();
  } catch {
    /* ignore */
  }
  try {
    if (typeof window !== "undefined") {
      const toDrop: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && key.startsWith("thiia:cache:")) toDrop.push(key);
      }
      toDrop.forEach((k) => window.localStorage.removeItem(k));
    }
  } catch {
    /* ignore */
  }
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("v", Date.now().toString(36));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

export function startVersionChecker() {
  if (started) return;
  started = true;

  // Skip in dev / preview iframes to avoid noise.
  if (import.meta.env.DEV) return;

  // Kick off a check shortly after boot, then poll.
  window.setTimeout(() => void checkOnce(), 15_000);
  window.setInterval(() => void checkOnce(), CHECK_INTERVAL_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      lastHiddenAt = Date.now();
    } else {
      void checkOnce();
    }
  });

  window.addEventListener("focus", () => void checkOnce());
}

export const BUILD_ID = CURRENT_BUILD_ID;
